import { useEffect, useState } from 'react'
import { ApiError, decideApproval, peekApproval } from '../api'
import { useT } from '../lang'

/* Letting a device in, from the email, on a phone, without signing in.
 *
 * This page is the lock. The enrollment code it replaced was twelve random
 * characters and defended itself; the code is now something a manager chose so
 * a worker could remember it, which means it is guessable -- every locality a
 * Norwegian company owns is in a public register by name, so "Ljo2" is a few
 * dozen candidates rather than a million. Nothing downstream of here defends
 * the code. This does.
 *
 * WHY IT DOES NOT DECIDE ON LOAD. It would be one fewer tap, and the tap it
 * removed is the only one that means anything. Two reasons, and the second is
 * the one that would actually bite:
 *
 *   1. A decision needs its facts in front of it. Which device, which farm,
 *      when, from what address. Approving something you were not shown is not
 *      review, and review is now the entire security model.
 *
 *   2. Mail providers follow links. Defender, Gmail's proxy, every corporate
 *      scanner -- they fetch what arrives in an inbox to see where it goes. The
 *      token rides in the fragment so that fetch cannot reach it, but a page
 *      that acted on load would still be acted on by anything that rendered it.
 *      Approving devices on the scanner's behalf is a failure that looks, from
 *      the office, exactly like approval working.
 */

type Peeked = Awaited<ReturnType<typeof peekApproval>>

function useHashToken(): string {
  const [token] = useState(() => window.location.hash.replace(/^#/, ''))
  useEffect(() => {
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname)
    }
  }, [])
  return token
}

export function Godkjenn() {
  const t = useT()
  const token = useHashToken()
  const [what, setWhat] = useState<Peeked | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<'approve' | 'decline' | null>(null)

  useEffect(() => {
    if (!token) { setError(t('That link is incomplete.')); return }
    peekApproval(token)
      .then(setWhat)
      .catch((e) => setError(
        e instanceof ApiError
          ? (e.code === 'already_decided'
              ? t('That device has already been dealt with.')
              : e.code === 'token_invalid'
                ? t('That link has expired or has already been used. You can still find the device on the Devices page.')
                : (e.detail ?? e.code))
          : t('Could not reach the receiver.')))
  }, [token])

  const decide = async (action: 'approve' | 'decline') => {
    setBusy(true)
    setError(null)
    try {
      await decideApproval(token, action)
      setDone(action)
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.code)
        : t('Could not reach the receiver.'))
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap">
      <div className="login">
        {done ? (
          <div className="card stack" style={{ gap: 14 }}>
            <h2 style={{ margin: 0, fontSize: 20 }}>
              {done === 'approve' ? t('Let in.') : t('Turned away.')}
            </h2>
            <p className="muted" style={{ margin: 0, fontSize: 14 }}>
              {done === 'approve'
                ? t('It can start counting straight away — the screen it is waiting on will move on by itself.')
                : t('It stops at its very next request. Nothing it had is kept.')}
            </p>
            <a className="btn" href="/enheter">{t('See the devices')}</a>
          </div>
        ) : error ? (
          <div className="card stack" style={{ gap: 14 }}>
            <div className="notice bad" style={{ margin: 0 }}>{error}</div>
            <a className="btn" href="/enheter">{t('See the devices')}</a>
          </div>
        ) : !what ? (
          <div className="card"><p className="muted" style={{ margin: 0 }}>
            {t('Looking it up…')}
          </p></div>
        ) : (
          <div className="card stack" style={{ gap: 18 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20 }}>
                {what.rescope
                  ? t('Give this device wider access?')
                  : t('Let this device in?')}
              </h2>
              <p className="muted" style={{ margin: '6px 0 0', fontSize: 14 }}>
                {what.rescope
                  ? t('It is already counting for you and has asked for more.')
                  : t('Somebody used your connection code.')}
              </p>
            </div>

            {/* The facts, laid out rather than written into a sentence. This is
                what somebody is deciding on, and a manager who does not
                recognise one of these lines is exactly the case this page
                exists for. */}
            <dl className="stack" style={{ gap: 10, margin: 0 }}>
              <Fact label={t('Device')}
                value={what.display_name || t('unnamed')} />
              <Fact label={t('Wants to count at')} value={what.scope} />
              <Fact label={t('Company')} value={what.org_name} />
              {what.code_label && (
                <Fact label={t('Code used')} value={what.code_label} />
              )}
              {what.ip && (
                /* The one fact that can be checked against the world. Not
                   proof of anything, and not presented as any. */
                <Fact label={t('Coming from')} value={what.ip} />
              )}
              {what.asked_at && (
                <Fact label={t('Asked')}
                  value={new Date(what.asked_at).toLocaleString()} />
              )}
            </dl>

            <div className="row" style={{ gap: 8 }}>
              <button className="btn primary" disabled={busy}
                onClick={() => void decide('approve')}>
                {busy ? t('One moment…') : t('Let it in')}
              </button>
              <button className="btn ghost" disabled={busy}
                onClick={() => void decide('decline')}>
                {t('Turn it away')}
              </button>
            </div>

            <p className="faint" style={{ margin: 0, fontSize: 12 }}>
              {t('If you were not expecting this, turn it away. The code is meant to be easy to remember, so it is not a secret — this is.')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ gap: 12, justifyContent: 'space-between' }}>
      <dt className="muted" style={{ fontSize: 13 }}>{label}</dt>
      <dd style={{ margin: 0, fontWeight: 600, textAlign: 'right' }}>{value}</dd>
    </div>
  )
}
